/* eslint-disable */
import { colors } from '../../helper/constant/colors';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import meya_logo from '../../assets/images/logo_meya.png';
import Policy from '../login/Policy';
import {
  formatSalary,
  formatShortDate,
  getJobCurrentUser,
  resolveMapCoords,
  resolveJobCategory,
  resolveJobPosterImages,
  getFriendlyErrorMsg,
} from './jobUtils';
import './JobDetail.css';

const DEFAULT_BENEFITS = [
  { text: 'ประกันสังคม', icon: 'mdi-shield-plus-outline' },
  { text: 'โบนัสประจำปี', icon: 'mdi-gift-outline' },
  { text: 'วันหยุดพักผ่อน', icon: 'mdi-calendar-month-outline' },
  { text: 'ชุดฟอร์มพนักงาน', icon: 'mdi-tshirt-crew-outline' },
];

const renderLinkifiedText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: colors.sky500, textDecoration: 'underline', wordBreak: 'break-all' }}>
          {part}
        </a>
      );
    }
    return part;
  });
};

function JobDetail({ permitdata, isUserLoggedIn, regName, regEmail, open_signin, app_hostname }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const resumeInputRef = useRef(null);
  const extraInputRef = useRef(null);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [isCommentAnonymous, setIsCommentAnonymous] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');

  const fetchComments = async (jobId) => {
    const targetId = jobId || id;
    if (!targetId) return;
    setIsCommentsLoading(true);
    try {
      const res = await axios.post((app_hostname || '') + '/api/market/jobs/comments/list', {
        job_id: String(targetId),
      });
      if (res.data && res.data.status === 'Success') {
        setComments(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchJobDetail = async () => {
      setLoading(true);
      setJob(null);
      try {
        const res = await axios.post((app_hostname || '') + '/api/market/jobs/detail', {
          id: id ? String(id) : '',
        });
        if (cancelled) return;
        if (res.data && res.data.status === 'Success' && res.data.data) {
          const d = res.data.data;
          const reqsList = d.requirements
            ? d.requirements.split('\n').filter(Boolean)
            : ['จบการศึกษาตามสายวิชาชีพ'];
          const benefitsList = d.benefits
            ? d.benefits
                .split(',')
                .map((b) => ({ text: b.trim(), icon: 'mdi-shield-plus-outline' }))
            : DEFAULT_BENEFITS;

          const resolveImgUrl = (url, fallback) => {
            if (!url) return fallback;
            if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:'))
              return url;
            return (app_hostname || '') + url;
          };

          const coords = resolveMapCoords({
            id: d.job_id || d.id,
            lat: d.lat,
            lng: d.lng,
            province: d.province,
            address: d.address,
            district: d.district,
            title: d.title,
            company: d.company_name,
          });

          const logoUrl = resolveImgUrl(d.company_logo, meya_logo);
          const resolveImgList = (urls) =>
            (urls || []).map((img) => resolveImgUrl(img, logoUrl)).filter(Boolean);

          const rawGallery = Array.isArray(d.gallery_images)
            ? resolveImgList(d.gallery_images)
            : d.gallery_images;
          const posterResolved = d.poster_image ? resolveImgUrl(d.poster_image, logoUrl) : '';
          const posterState = resolveJobPosterImages({
            jobId: d.job_id || d.id,
            posterImage: posterResolved,
            galleryImages: rawGallery,
            logoUrl,
            preferLogoWhenEmpty: true,
          });

          setJob({
            id: d.job_id || d.id,
            title: d.title,
            company: d.company_name,
            location: d.province,
            salary: formatSalary(d.salary_min, d.salary_max, d.salary_text, d.salary_negotiable),
            type: d.job_type || 'Full Time',
            category: resolveJobCategory(d.title, d.category_name),
            posted: formatShortDate(d.created_at),
            views: d.view_count || 1,
            logo: logoUrl,
            cover: posterState.cover,
            galleryImages: posterState.images,
            hasCustomPoster: posterState.hasCustomPoster,
            industry: resolveJobCategory(d.title, d.category_name) || 'สาธารณสุข/การแพทย์',
            employees: 'สถานพยาบาล/ร้านยา',
            website: d.company_website || '',
            address: d.address || `${d.district || ''} ${d.province || ''}`,
            lat: coords.lat ?? 13.7563,
            lng: coords.lng ?? 100.5018,
            desc: d.description || 'ไม่มีรายละเอียดเพิ่มเติม',
            reqs: reqsList,
            benefits: benefitsList,
            summary: {
              salaryRange: formatSalary(
                d.salary_min,
                d.salary_max,
                d.salary_text,
                d.salary_negotiable,
              ),
              jobType: d.job_type,
              experience: d.experience_required || 'ไม่ระบุ',
              education: d.education_level || 'ปริญญาตรี',
              locationFull: d.address || d.province,
            },
            employer_id: d.employer_id || '',
            shop_id: d.shop_id || '',
          });

          fetchComments(d.job_id || d.id);

          // Check if current user bookmarked this job
          const { userId: currentUserId } = getJobCurrentUser(permitdata);
          if (currentUserId) {
            axios
              .post((app_hostname || '') + '/api/market/jobs/my_bookmarks', {
                user_id: currentUserId,
              })
              .then((bmRes) => {
                if (cancelled) return;
                if (bmRes.data && bmRes.data.status === 'Success' && bmRes.data.data?.items) {
                  const isBm = bmRes.data.data.items.some(
                    (item) => String(item.job_id || item.id) === String(d.job_id || d.id),
                  );
                  setIsSaved(isBm);
                }
              })
              .catch(() => {});
          }
        }
      } catch (err) {
        // Fallback — leave job null so not-found UI shows after loading
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) {
      fetchJobDetail();
    } else {
      setLoading(false);
      setJob(null);
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  const [applicantName, setApplicantName] = useState(
    regName || permitdata?.user_name || permitdata?.shop_name || '',
  );
  const [applicantPhone, setApplicantPhone] = useState(
    permitdata?.shop_phone || permitdata?.phone || '',
  );
  const [applicantEmail, setApplicantEmail] = useState(
    regEmail || permitdata?.shop_email || permitdata?.email || '',
  );
  const [applicantLineId, setApplicantLineId] = useState('');
  const [applicantCover, setApplicantCover] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [extraFileName, setExtraFileName] = useState('');
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isSubmittingApply, setIsSubmittingApply] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [appliedSuccessId, setAppliedSuccessId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isApplyPolicyAccepted, setIsApplyPolicyAccepted] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  useEffect(() => {
    if (regName || permitdata?.user_name || permitdata?.shop_name) {
      setApplicantName(regName || permitdata?.user_name || permitdata?.shop_name || '');
    }
    if (regEmail || permitdata?.shop_email || permitdata?.email) {
      setApplicantEmail(regEmail || permitdata?.shop_email || permitdata?.email || '');
    }
    if (permitdata?.shop_phone || permitdata?.phone) {
      setApplicantPhone(permitdata?.shop_phone || permitdata?.phone || '');
    }
  }, [regName, regEmail, permitdata]);

  const jobImages = (() => {
    if (!job) return [];
    if (Array.isArray(job.galleryImages) && job.galleryImages.length > 0) {
      return job.galleryImages;
    }
    if (job.cover) return [job.cover];
    if (job.logo) return [job.logo];
    return [meya_logo];
  })();
  const isLogoPlaceholder = job && !job.hasCustomPoster;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const nextImage = () => {
    if (jobImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % jobImages.length);
  };
  const prevImage = () => {
    if (jobImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + jobImages.length) % jobImages.length);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentImageIndex(0);
  }, [id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPreviewOpen(false);
        if (!isSubmittingApply) setIsApplyModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSubmittingApply]);

  const ensureLoggedIn = () => {
    if (isUserLoggedIn) return true;
    if (open_signin) {
      open_signin();
    }
    return false;
  };

  const handleApplyClick = () => {
    if (!ensureLoggedIn()) return;
    setApplyError('');
    setIsApplyPolicyAccepted(false);
    setIsApplyModalOpen(true);
  };

  const handleApplySubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!ensureLoggedIn()) return;
    const seekerId = getJobCurrentUser(permitdata).userId;
    if (!seekerId) {
      setApplyError('ไม่พบข้อมูลผู้สมัคร กรุณาเข้าสู่ระบบอีกครั้ง');
      return;
    }
    if (!applicantName.trim()) {
      setApplyError('กรุณากรอกชื่อ-นามสกุลของผู้สมัคร');
      return;
    }
    if (!applicantPhone.trim()) {
      setApplyError('กรุณากรอกเบอร์โทรศัพท์ที่สามารถติดต่อได้');
      return;
    }
    if (!isApplyPolicyAccepted) {
      setApplyError('กรุณายอมรับเงื่อนไขการใช้บริการก่อนส่งใบสมัคร');
      return;
    }

    setIsSubmittingApply(true);
    setApplyError('');
    try {
      const res = await axios.post((app_hostname || '') + '/api/market/jobs/apply', {
        job_id: job?.id || id,
        seeker_id: seekerId,
        applicant_name: applicantName.trim(),
        applicant_phone: applicantPhone.trim(),
        applicant_email: applicantEmail.trim(),
        applicant_line_id: applicantLineId.trim(),
        cover_letter: applicantCover.trim(),
        resume_url: resumeUrl || resumeFileName || 'Uploaded_Resume.pdf',
      });
      if (res.data && res.data.status === 'Success') {
        const appId = res.data.data?.application_id || 'APP-SUCCESS';
        setAppliedSuccessId(appId);
      } else {
        setApplyError(getFriendlyErrorMsg(res.data?.message, 'เกิดข้อผิดพลาดในการส่งใบสมัคร กรุณาตรวจสอบข้อมูล'));
      }
    } catch (err) {
      setApplyError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmittingApply(false);
    }
  };

  const handleSaveJob = async () => {
    if (!ensureLoggedIn()) return;
    const { userId: currentUserId } = getJobCurrentUser(permitdata);
    const currentJobId = job?.id || id;
    if (!currentUserId || !currentJobId) {
      return;
    }
    try {
      const res = await axios.post((app_hostname || '') + '/api/market/jobs/bookmark_toggle', {
        user_id: currentUserId,
        job_id: currentJobId,
      });
      if (res.data && res.data.status === 'Success') {
        setIsSaved(Boolean(res.data.data?.is_bookmarked));
      }
    } catch (e) {
      console.error('[Bookmark] Toggle error:', e);
    }
  };

  const shopData = (() => {
    if (permitdata && permitdata.shop_id && permitdata.shop_id !== '-') {
      return permitdata;
    }
    try {
      const stored = sessionStorage.getItem('meya-token4');
      if (stored && stored !== 'undefined') {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.shop_id && parsed.shop_id !== '-') {
          return parsed;
        }
      }
    } catch (e) {
      // ignore parse error
    }
    return null;
  })();

  const isShopMember = Boolean(
    shopData && shopData.role !== 'guest' && shopData.token && shopData.token !== '-',
  );
  const currentShopId = shopData?.shop_id || '';
  const currentShopName = shopData?.shop_name || regName || '';
  const currentUserId = getJobCurrentUser(permitdata).userId || currentShopId || '';

  const isPostEmployer = Boolean(
    (currentShopId &&
      (String(currentShopId).trim().toLowerCase() ===
        String(job?.shop_id || '')
          .trim()
          .toLowerCase() ||
        String(currentShopId).trim().toLowerCase() ===
          String(job?.employer_id || '')
            .trim()
            .toLowerCase())) ||
      (currentUserId &&
        (String(currentUserId).trim().toLowerCase() ===
          String(job?.shop_id || '')
            .trim()
            .toLowerCase() ||
          String(currentUserId).trim().toLowerCase() ===
            String(job?.employer_id || '')
              .trim()
              .toLowerCase())),
  );

  const canDeleteComment = (c) =>
    Boolean(
      (c.user_id && currentUserId && String(c.user_id) === String(currentUserId)) || isPostEmployer,
    );

  const handleCommentSubmit = async () => {
    const text = commentText.trim();
    if (!text) return;
    if (!ensureLoggedIn()) return;

    setIsCommentSubmitting(true);
    setCommentError('');
    try {
      let author = '';
      let userType = 'seeker';
      let defaultAnonName = 'ผู้หางาน (ไม่เปิดเผยชื่อ)';

      if (isPostEmployer) {
        author = job?.company || currentShopName || 'ผู้ประกอบการ / ร้านค้า';
        userType = 'employer';
      } else if (isShopMember) {
        author = currentShopName || 'ร้านค้า/ผู้ประกอบการ';
        userType = 'employer';
        defaultAnonName = 'ร้านค้า/สถานประกอบการ (ไม่เปิดเผยชื่อ)';
      } else {
        author = applicantName || regName || permitdata?.user_name || 'ผู้หางาน';
        userType = 'seeker';
        defaultAnonName = 'ผู้หางาน (ไม่เปิดเผยชื่อ)';
      }

      const payload = {
        job_id: job?.id || id,
        user_id: currentUserId,
        user_type: userType,
        author_name: isPostEmployer ? author : isCommentAnonymous ? defaultAnonName : author,
        author_avatar: isPostEmployer ? job?.logo || '' : '',
        parent_comment_id: null,
        message: text,
        is_anonymous: isPostEmployer ? false : isCommentAnonymous,
      };

      const res = await axios.post(
        (app_hostname || '') + '/api/market/jobs/comments/create',
        payload,
      );
      if (res.data && res.data.status === 'Success') {
        setCommentText('');
        fetchComments(job?.id || id);
      } else {
        setCommentError(getFriendlyErrorMsg(res.data?.message, 'ไม่สามารถส่งข้อความได้ กรุณาลองใหม่'));
      }
    } catch (err) {
      setCommentError('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const handleReplySubmit = async (parentId) => {
    const text = replyText.trim();
    if (!text) return;
    if (!ensureLoggedIn()) return;

    setIsReplySubmitting(true);
    try {
      let author = '';
      let userType = 'seeker';

      if (isPostEmployer) {
        author = job?.company || currentShopName || 'ผู้ประกอบการ / ร้านค้า';
        userType = 'employer';
      } else if (isShopMember) {
        author = currentShopName || 'ร้านค้า/ผู้ประกอบการ';
        userType = 'employer';
      } else {
        author = applicantName || regName || permitdata?.user_name || 'ผู้ใช้งาน';
        userType = 'seeker';
      }

      const payload = {
        job_id: job?.id || id,
        user_id: currentUserId,
        user_type: userType,
        author_name: author,
        author_avatar: isPostEmployer ? job?.logo || '' : '',
        parent_comment_id: parentId,
        message: text,
        is_anonymous: false,
      };

      const res = await axios.post(
        (app_hostname || '') + '/api/market/jobs/comments/create',
        payload,
      );
      if (res.data && res.data.status === 'Success') {
        setReplyText('');
        setReplyingToId(null);
        fetchComments(job?.id || id);
      } else {
        alert(getFriendlyErrorMsg(res.data?.message, 'ไม่สามารถส่งคำตอบกลับได้'));
      }
    } catch (err) {
      alert('เกิดข้อผิดพลาดในการส่งข้อความตอบกลับ');
    } finally {
      setIsReplySubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('คุณต้องการลบข้อความนี้ใช่หรือไม่?')) return;
    try {
      const res = await axios.post((app_hostname || '') + '/api/market/jobs/comments/delete', {
        comment_id: commentId,
        user_id: currentUserId,
        shop_id: currentShopId,
      });
      if (res.data && res.data.status === 'Success') {
        fetchComments(job?.id || id);
      } else {
        alert(getFriendlyErrorMsg(res.data?.message, 'ไม่สามารถลบความคิดเห็นได้'));
      }
    } catch (err) {
      alert('เกิดข้อผิดพลาดในการลบความคิดเห็น');
    }
  };

  if (loading) {
    return (
      <div className='job-detail-page'>
        <div className='job-not-found'>
          <i className='mdi mdi-loading mdi-spin'></i>
          <h2>กำลังโหลดตำแหน่งงาน...</h2>
          <p>กรุณารอสักครู่</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className='job-detail-page'>
        <div className='job-not-found'>
          <i className='mdi mdi-briefcase-off-outline'></i>
          <h2>ไม่พบตำแหน่งงานนี้</h2>
          <p>รหัสงานที่คุณเปิดอาจถูกลบหรือไม่มีในระบบ</p>
          <button type='button' className='btn-apply' onClick={() => navigate('/jobs')}>
            กลับไปค้นหางาน
          </button>
        </div>
      </div>
    );
  }

  const summary = job.summary || {};

  return (
    <div className='job-detail-page'>
      <div className='breadcrumb-nav'>
        <span onClick={() => navigate('/')}>หน้าหลัก</span>
        <span className='separator'>&gt;</span>
        <span onClick={() => navigate('/jobs')}>หางาน</span>
        <span className='separator'>&gt;</span>
        <span className='current'>{job.company}</span>
      </div>

      <div className='job-detail-grid'>
        <div className='left-column'>
          <div className='card banner-card'>
            <div className='image-slider'>
              <img
                src={jobImages[currentImageIndex]}
                alt={job.title}
                className={`slider-image clickable${isLogoPlaceholder ? ' logo-placeholder' : ''}`}
                onClick={() => setIsPreviewOpen(true)}
                title='คลิกเพื่อดูภาพใหญ่'
                onError={(e) => {
                  if (e.target.src !== (job.logo || meya_logo)) {
                    e.target.src = job.logo || meya_logo;
                  }
                }}
              />
              <div className='zoom-hint-badge' onClick={() => setIsPreviewOpen(true)}>
                <i className='mdi mdi-magnify-plus-outline'></i>{' '}
                {isLogoPlaceholder ? 'แสดงโลโก้ร้าน (ยังไม่มีรูปโปสเตอร์)' : 'คลิกเพื่อขยายรูป'}
              </div>

              {jobImages.length > 1 && (
                <>
                  <button
                    type='button'
                    className='slider-btn prev-btn'
                    onClick={prevImage}
                    aria-label='ภาพก่อนหน้า'
                  >
                    <i className='mdi mdi-chevron-left'></i>
                  </button>
                  <button
                    type='button'
                    className='slider-btn next-btn'
                    onClick={nextImage}
                    aria-label='ภาพถัดไป'
                  >
                    <i className='mdi mdi-chevron-right'></i>
                  </button>

                  <div className='slider-dots'>
                    {jobImages.map((_, idx) => (
                      <span
                        key={idx}
                        className={`dot ${currentImageIndex === idx ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(idx)}
                      ></span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className='card company-card'>
            <h4>เกี่ยวกับบริษัท</h4>
            <div className='company-header'>
              <img src={job.logo} alt='logo' className='company-logo-lg' />
              <div className='company-name-lg'>{job.company}</div>
            </div>
            <ul className='company-details'>
              <li>
                <div className='label-group'>
                  <i className='mdi mdi-domain'></i>
                  <span className='label'>อุตสาหกรรม</span>
                </div>
                <span className='value'>{job.industry || 'โรงพยาบาล/การแพทย์'}</span>
              </li>
              <li>
                <div className='label-group'>
                  <i className='mdi mdi-account-group'></i>
                  <span className='label'>พนักงาน</span>
                </div>
                <span className='value'>{job.employees || '-'}</span>
              </li>
              <li>
                <div className='label-group'>
                  <i className='mdi mdi-web'></i>
                  <span className='label'>เว็บไซต์</span>
                </div>
                {job.website && job.website !== '-' ? (
                  <a href={job.website.startsWith('http') ? job.website : 'https://' + job.website} target='_blank' rel='noopener noreferrer' className='value link' style={{ color: colors.sky500, textDecoration: 'underline', wordBreak: 'break-all' }}>{job.website}</a>
                ) : (
                  <span className='value link'>-</span>
                )}
              </li>
              <li>
                <div className='label-group'>
                  <i className='mdi mdi-map-marker-outline'></i>
                  <span className='label'>ที่อยู่</span>
                </div>
                <span className='value'>{job.address || job.location}</span>
              </li>
            </ul>
            <div className='map-placeholder' style={{ marginTop: '20px' }}>
              <iframe
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${((job.lng || 100.527) - 0.008).toFixed(6)}%2C${((job.lat || 13.7285) - 0.005).toFixed(6)}%2C${((job.lng || 100.527) + 0.008).toFixed(6)}%2C${((job.lat || 13.7285) + 0.005).toFixed(6)}&layer=mapnik&marker=${job.lat || 13.7285}%2C${job.lng || 100.527}`}
                width='100%'
                height='160'
                style={{ border: 0, borderRadius: '8px', display: 'block' }}
                allowFullScreen=''
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
                title='company-location-map'
              ></iframe>
              <div style={{ marginTop: '10px' }}>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${job.lat || 13.7285},${job.lng || 100.527}`}
                  target='_blank'
                  rel='noreferrer'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: colors.sky600,
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#e0f2fe';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#f0f9ff';
                  }}
                >
                  <i className='mdi mdi-map-marker-radius' style={{ fontSize: '16px' }}></i>
                  เปิดนำทางด้วย Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className='right-column'>
          <div className='card main-info-card'>
            <div className='main-info-header'>
              <div className='badges-row'>
                <span className='badge badge-outline-blue'>{job.category}</span>
                <span className='badge badge-outline-teal'>เปิดรับสมัคร</span>
              </div>
              <div className='action-btns'>
                {isPostEmployer ? (
                  <button
                    type='button'
                    className='btn-apply'
                    onClick={() =>
                      navigate(`/jobs/employer?tab=post_job&edit=${encodeURIComponent(job.id)}`)
                    }
                  >
                    <i className='mdi mdi-pencil-box-outline'></i> จัดการประกาศงานนี้
                  </button>
                ) : (
                  <button type='button' className='btn-apply' onClick={handleApplyClick}>
                    <i className='mdi mdi-send'></i> สมัครงาน
                  </button>
                )}
                <button
                  type='button'
                  className={`btn-save ${isSaved ? 'saved' : ''}`}
                  onClick={handleSaveJob}
                >
                  <i className={`mdi ${isSaved ? 'mdi-heart' : 'mdi-heart-outline'}`}></i>{' '}
                  {isSaved ? 'บันทึกแล้ว' : 'บันทึกงาน'}
                </button>
              </div>
            </div>

            <h1 className='job-detail-title'>{job.title}</h1>

            <div className='meta-row'>
              <span>
                <i className='mdi mdi-map-marker'></i> {job.location}
              </span>
              <span>
                <i className='mdi mdi-clock-outline'></i> {job.type}
              </span>
              <span>
                <i className='mdi mdi-clock-time-four-outline'></i> โพสต์เมื่อ {job.posted}
              </span>
              <span style={{ color: colors.blue600, fontWeight: 600 }}>
                <i className='mdi mdi-eye-outline'></i> มีผู้เข้าชมแล้ว{' '}
                {(job.views || 0).toLocaleString()} ครั้ง
              </span>
            </div>

            <div className='section-title'>สรุปข้อมูลตำแหน่งงาน</div>
            <div className='summary-grid'>
              <div className='summary-item'>
                <div className='s-label'>เงินเดือน</div>
                <div className='s-value text-green'>{summary.salaryRange || job.salary}</div>
              </div>
              <div className='summary-item'>
                <div className='s-label'>ประเภทงาน</div>
                <div className='s-value'>{summary.jobType || job.type}</div>
              </div>
              <div className='summary-item'>
                <div className='s-label'>ประสบการณ์</div>
                <div className='s-value'>{summary.experience || '-'}</div>
              </div>
              <div className='summary-item'>
                <div className='s-label'>ระดับการศึกษา</div>
                <div className='s-value'>{summary.education || '-'}</div>
              </div>
              <div className='summary-item'>
                <div className='s-label'>สถานที่ปฏิบัติงาน</div>
                <div className='s-value'>{summary.locationFull || job.location}</div>
              </div>
            </div>

            <div className='section-title'>
              <i className='mdi mdi-file-document' style={{ color: colors.blueLight }}></i> รายละเอียดงาน
            </div>
            <p className='desc-text' style={{ whiteSpace: 'pre-line' }}>{renderLinkifiedText(job.desc)}</p>

            <div className='section-title'>หน้าที่ความรับผิดชอบ</div>
            <ul className='check-list'>
              {(job.reqs || []).map((req, idx) => (
                <li key={idx}>
                  <i className='mdi mdi-check-circle' style={{ color: colors.blueLight }}></i> {renderLinkifiedText(req)}
                </li>
              ))}
            </ul>

            <div className='section-title'>
              <i className='mdi mdi-heart-circle' style={{ color: colors.blueLight }}></i> สวัสดิการ
            </div>
            <div className='benefits-tags'>
              {(job.benefits || DEFAULT_BENEFITS).map((b, idx) => (
                <span className='b-tag' key={idx}>
                  <i className={`mdi ${b.icon || 'mdi-check'}`}></i> {b.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className='bottom-cards'>
        <div className='card comments-card'>
          <div className='section-title-wrapper'>
            <div className='section-title'>
              <i className='mdi mdi-forum-outline' style={{ color: colors.blueLight }}></i>{' '}
              คำถามและข้อสงสัยเกี่ยวกับตำแหน่งงานนี้
              <span className='comment-counter-badge'>
                {comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)}
              </span>
            </div>
            <p className='section-subtitle'>
              พื้นที่สาธารณะสำหรับสอบถามรายละเอียดงานกับผู้ประกอบการโดยตรง
              (สามารถเลือกโพสต์แบบไม่เปิดเผยชื่อได้)
            </p>
          </div>

          <div className='comment-list'>
            {isCommentsLoading ? (
              <div className='comments-loading'>
                <i className='mdi mdi-loading mdi-spin'></i> กำลังโหลดข้อความความคิดเห็น...
              </div>
            ) : comments.length === 0 ? (
              <div className='comments-empty'>
                <i className='mdi mdi-chat-question-outline'></i>
                <p>
                  ยังไม่มีคำถามเกี่ยวกับตำแหน่งงานนี้ เป็นคนแรกที่เริ่มสอบถามผู้ประกอบการได้เลย!
                </p>
              </div>
            ) : (
              comments.map((c) => (
                <div className='comment-thread-group' key={c.comment_id}>
                  {/* Top-level Question */}
                  <div
                    className={`comment-item ${c.user_type === 'employer' ? 'comment-employer' : ''}`}
                  >
                    <div className='c-avatar'>
                      {c.is_anonymous ? (
                        <div className='c-avatar-anon' title='ไม่เปิดเผยชื่อ'>
                          <i className='mdi mdi-incognito'></i>
                        </div>
                      ) : (
                        <img
                          src={c.author_avatar || meya_logo}
                          alt='avatar'
                          onError={(e) => {
                            e.target.src = meya_logo;
                          }}
                        />
                      )}
                    </div>
                    <div className='c-content'>
                      <div className='c-head'>
                        <div className='c-author-info'>
                          <strong className='c-author-name'>{c.author_name}</strong>
                          {c.user_type === 'employer' ? (
                            <span className='c-badge badge-employer'>
                              <i className='mdi mdi-store-check'></i> ผู้ประกอบการ / ร้านค้า
                            </span>
                          ) : c.is_anonymous ? (
                            <span className='c-badge badge-anon'>
                              <i className='mdi mdi-incognito'></i> ไม่เปิดเผยชื่อ
                            </span>
                          ) : (
                            <span className='c-badge badge-seeker'>
                              <i className='mdi mdi-account-outline'></i> ผู้หางาน
                            </span>
                          )}
                        </div>
                        <div className='c-actions'>
                          <span className='c-time'>{c.created_at}</span>
                          {canDeleteComment(c) && (
                            <button
                              type='button'
                              className='btn-comment-delete'
                              title='ลบข้อความนี้'
                              onClick={() => handleDeleteComment(c.comment_id)}
                            >
                              <i className='mdi mdi-trash-can-outline'></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className='c-text'>{c.message}</div>

                      {/* Reply button */}
                      <div className='c-footer'>
                        <button
                          type='button'
                          className='btn-reply-toggle'
                          onClick={() => {
                            if (replyingToId === c.comment_id) {
                              setReplyingToId(null);
                              setReplyText('');
                            } else {
                              setReplyingToId(c.comment_id);
                              setReplyText('');
                            }
                          }}
                        >
                          <i className='mdi mdi-reply'></i> ตอบกลับ
                        </button>
                      </div>

                      {/* Inline Reply Form */}
                      {replyingToId === c.comment_id && (
                        <div className='reply-input-box'>
                          <div className='reply-box-header'>
                            <span>
                              ตอบกลับ <strong>{c.author_name}</strong>
                            </span>
                            {isPostEmployer && (
                              <span className='badge-employer-sm'>
                                ตอบในนาม: {job?.company || currentShopName || 'ผู้ประกอบการ'}
                              </span>
                            )}
                            {!isPostEmployer && isShopMember && (
                              <span
                                className='badge-employer-sm'
                                style={{
                                  background: '#ecfdf5',
                                  borderColor: '#a7f3d0',
                                  color: '#065f46',
                                }}
                              >
                                ตอบในนามร้านค้า: {currentShopName}
                              </span>
                            )}
                          </div>
                          <div className='reply-input-row'>
                            <input
                              type='text'
                              placeholder='พิมพ์ข้อความตอบกลับของคุณ...'
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleReplySubmit(c.comment_id);
                                }
                              }}
                              autoFocus
                            />
                            <button
                              type='button'
                              className='btn-reply-cancel'
                              onClick={() => {
                                setReplyingToId(null);
                                setReplyText('');
                              }}
                            >
                              ยกเลิก
                            </button>
                            <button
                              type='button'
                              className='btn-reply-submit'
                              disabled={isReplySubmitting || !replyText.trim()}
                              onClick={() => handleReplySubmit(c.comment_id)}
                            >
                              {isReplySubmitting ? 'กำลังส่ง...' : 'ส่งคำตอบ'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Threaded Child Replies */}
                  {c.replies && c.replies.length > 0 && (
                    <div className='comment-replies'>
                      {c.replies.map((reply) => (
                        <div
                          className={`comment-item comment-reply-item ${reply.user_type === 'employer' ? 'comment-employer' : ''}`}
                          key={reply.comment_id}
                        >
                          <div className='c-avatar'>
                            {reply.is_anonymous ? (
                              <div className='c-avatar-anon' title='ไม่เปิดเผยชื่อ'>
                                <i className='mdi mdi-incognito'></i>
                              </div>
                            ) : (
                              <img
                                src={reply.author_avatar || meya_logo}
                                alt='avatar'
                                onError={(e) => {
                                  e.target.src = meya_logo;
                                }}
                              />
                            )}
                          </div>
                          <div className='c-content'>
                            <div className='c-head'>
                              <div className='c-author-info'>
                                <strong className='c-author-name'>{reply.author_name}</strong>
                                {reply.user_type === 'employer' ? (
                                  <span className='c-badge badge-employer'>
                                    <i className='mdi mdi-store-check'></i> ผู้ประกอบการ / ร้านค้า
                                  </span>
                                ) : reply.is_anonymous ? (
                                  <span className='c-badge badge-anon'>
                                    <i className='mdi mdi-incognito'></i> ไม่เปิดเผยชื่อ
                                  </span>
                                ) : (
                                  <span className='c-badge badge-seeker'>
                                    <i className='mdi mdi-account-outline'></i> ผู้หางาน
                                  </span>
                                )}
                              </div>
                              <div className='c-actions'>
                                <span className='c-time'>{reply.created_at}</span>
                                {canDeleteComment(reply) && (
                                  <button
                                    type='button'
                                    className='btn-comment-delete'
                                    title='ลบข้อความนี้'
                                    onClick={() => handleDeleteComment(reply.comment_id)}
                                  >
                                    <i className='mdi mdi-trash-can-outline'></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className='c-text'>{reply.message}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* New Question / Message Form */}
          <div className='comment-form-box'>
            {commentError && (
              <div className='comment-error-alert'>
                <i className='mdi mdi-alert-circle-outline'></i> {commentError}
              </div>
            )}
            <div className='comment-input'>
              <input
                type='text'
                placeholder={
                  isPostEmployer
                    ? 'พิมพ์ข้อความหรือชี้แจงเพิ่มเติมในนามร้านค้า/ผู้ประกอบการ...'
                    : isShopMember
                      ? 'พิมพ์ข้อความสอบถามในนามร้านค้า/ผู้ประกอบการ...'
                      : 'พิมพ์คำถามหรือข้อสงสัยเกี่ยวกับตำแหน่งงานนี้...'
                }
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCommentSubmit();
                  }
                }}
              />
              <button
                type='button'
                className='btn-comment-submit'
                disabled={isCommentSubmitting || !commentText.trim()}
                onClick={handleCommentSubmit}
              >
                {isCommentSubmitting ? (
                  <>
                    <i className='mdi mdi-loading mdi-spin'></i> กำลังส่ง...
                  </>
                ) : (
                  <>
                    <i className='mdi mdi-send'></i>{' '}
                    {isPostEmployer ? 'ส่งข้อความตอบกลับ' : 'ส่งคำถาม'}
                  </>
                )}
              </button>
            </div>

            <div className='comment-options-bar'>
              {isPostEmployer ? (
                <div className='employer-posting-notice'>
                  <i className='mdi mdi-shield-account'></i> คุณกำลังโพสต์ตอบกลับในฐานะ{' '}
                  <strong>{job?.company || currentShopName || 'ผู้ประกอบการ'}</strong>
                </div>
              ) : isShopMember ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
                >
                  <div
                    className='employer-posting-notice'
                    style={{ background: colors.green50, borderColor: colors.green200, color: colors.green800 }}
                  >
                    <i className='mdi mdi-store'></i> คุณกำลังสอบถามในฐานะร้านค้า/ผู้ประกอบการ{' '}
                    <strong>{currentShopName || 'ร้านค้า'}</strong>
                  </div>
                  <label className='anon-checkbox-label'>
                    <input
                      type='checkbox'
                      checked={isCommentAnonymous}
                      onChange={(e) => setIsCommentAnonymous(e.target.checked)}
                    />
                    <span>
                      <i className='mdi mdi-incognito'></i> ไม่เปิดเผยชื่อร้านค้าต่อสาธารณะ
                      (แสดงชื่อเป็น &ldquo;ร้านค้า/สถานประกอบการ (ไม่เปิดเผยชื่อ)&rdquo;)
                    </span>
                  </label>
                </div>
              ) : (
                <label className='anon-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={isCommentAnonymous}
                    onChange={(e) => setIsCommentAnonymous(e.target.checked)}
                  />
                  <span>
                    <i className='mdi mdi-incognito'></i> ไม่เปิดเผยชื่อของฉันต่อสาธารณะ
                    (แสดงชื่อเป็น &ldquo;ผู้หางาน (ไม่เปิดเผยชื่อ)&rdquo;)
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      {isPreviewOpen && (
        <div className='image-lightbox-overlay' onClick={() => setIsPreviewOpen(false)}>
          <div className='image-lightbox-content' onClick={(e) => e.stopPropagation()}>
            <button
              type='button'
              className='lightbox-close-btn'
              onClick={() => setIsPreviewOpen(false)}
              title='ปิด'
            >
              <i className='mdi mdi-close'></i>
            </button>
            <img
              src={jobImages[currentImageIndex]}
              alt='Enlarged view'
              className='lightbox-image'
              onError={(e) => {
                if (e.target.src !== (job.logo || meya_logo)) {
                  e.target.src = job.logo || meya_logo;
                }
              }}
            />
            {jobImages.length > 1 && (
              <>
                <button
                  type='button'
                  className='lightbox-nav-btn prev'
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  title='รูปก่อนหน้า'
                >
                  <i className='mdi mdi-chevron-left'></i>
                </button>
                <button
                  type='button'
                  className='lightbox-nav-btn next'
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  title='รูปถัดไป'
                >
                  <i className='mdi mdi-chevron-right'></i>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className='job-detail-mobile-sticky'>
        <button type='button' className='btn-save' onClick={handleSaveJob}>
          <i className={`mdi ${isSaved ? 'mdi-heart' : 'mdi-heart-outline'}`}></i>
        </button>
        {isPostEmployer ? (
          <button
            type='button'
            className='btn-apply'
            onClick={() =>
              navigate(`/jobs/employer?tab=post_job&edit=${encodeURIComponent(job.id)}`)
            }
          >
            <i className='mdi mdi-pencil-box-outline'></i> จัดการประกาศงานนี้
          </button>
        ) : (
          <button type='button' className='btn-apply' onClick={handleApplyClick}>
            <i className='mdi mdi-send'></i> สมัครงาน
          </button>
        )}
      </div>

      {/* Apply Modal */}
      {isApplyModalOpen && (
        <div
          className='job-apply-modal-overlay'
          onClick={() => !isSubmittingApply && setIsApplyModalOpen(false)}
        >
          <div className='job-apply-modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='job-apply-modal-header'>
              <div className='job-apply-modal-title-box'>
                <h3 className='job-apply-modal-title'>
                  <i className='mdi mdi-send-check'></i> ยื่นใบสมัครงาน
                </h3>
                <div className='job-apply-modal-subtitle'>
                  ตำแหน่ง <strong>{job.title}</strong> — {job.company}
                </div>
              </div>
              <button
                type='button'
                className='job-apply-modal-close'
                disabled={isSubmittingApply}
                onClick={() => setIsApplyModalOpen(false)}
              >
                <i className='mdi mdi-close'></i>
              </button>
            </div>

            {appliedSuccessId ? (
              <div className='job-apply-modal-success'>
                <div className='job-apply-success-icon'>
                  <i className='mdi mdi-check-circle'></i>
                </div>
                <h4>ส่งใบสมัครเรียบร้อยแล้ว!</h4>
                <p className='job-apply-success-id'>
                  รหัสใบสมัครของคุณ: <strong>{appliedSuccessId}</strong>
                </p>
                <p className='job-apply-success-desc'>
                  สถานประกอบการได้รับข้อมูลใบสมัครของคุณเรียบร้อยแล้ว
                  และจะติดต่อกลับตามข้อมูลที่ระบุไว้
                </p>
                <button
                  type='button'
                  className='btn-apply-success-done'
                  onClick={() => {
                    setIsApplyModalOpen(false);
                    setAppliedSuccessId(null);
                  }}
                >
                  ตกลง / ปิดหน้าต่าง
                </button>
              </div>
            ) : (
              <form onSubmit={handleApplySubmit} className='job-apply-form'>
                {applyError && (
                  <div className='job-apply-error-banner'>
                    <i className='mdi mdi-alert-circle-outline'></i> {applyError}
                  </div>
                )}

                <div className='job-apply-form-row'>
                  <div className='job-apply-form-group'>
                    <label>
                      ชื่อ - นามสกุล <span className='required-star'>*</span>
                    </label>
                    <div className='input-with-icon'>
                      <i className='mdi mdi-account-outline'></i>
                      <input
                        type='text'
                        placeholder='เช่น สมชาย ใจดี'
                        value={applicantName}
                        onChange={(e) => setApplicantName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className='job-apply-form-group'>
                    <label>
                      เบอร์โทรศัพท์ <span className='required-star'>*</span>
                    </label>
                    <div className='input-with-icon'>
                      <i className='mdi mdi-phone-outline'></i>
                      <input
                        type='tel'
                        placeholder='เช่น 0812345678'
                        value={applicantPhone}
                        onChange={(e) => setApplicantPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className='job-apply-form-row'>
                  <div className='job-apply-form-group'>
                    <label>อีเมล</label>
                    <div className='input-with-icon'>
                      <i className='mdi mdi-email-outline'></i>
                      <input
                        type='email'
                        placeholder='เช่น somchai@example.com'
                        value={applicantEmail}
                        onChange={(e) => setApplicantEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className='job-apply-form-group'>
                    <label>LINE ID</label>
                    <div className='input-with-icon'>
                      <i className='mdi mdi-chat-processing-outline'></i>
                      <input
                        type='text'
                        placeholder='เช่น somchai_line'
                        value={applicantLineId}
                        onChange={(e) => setApplicantLineId(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className='job-apply-form-group'>
                  <label>แนะนำตัวและประสบการณ์โดยย่อ</label>
                  <textarea
                    rows='3'
                    placeholder='บอกเล่าประวัติการทำงาน สรุปทักษะเด่น หรือสิ่งที่ต้องการแจ้งแก่ผู้จ้างงาน...'
                    value={applicantCover}
                    onChange={(e) => setApplicantCover(e.target.value)}
                  ></textarea>
                </div>

                <div className='job-apply-form-group'>
                  <label>แนบเรซูเม่ / ประวัติส่วนตัว (PDF, DOC, PNG, JPG)</label>
                  <div className='file-upload-box'>
                    <input
                      type='file'
                      ref={resumeInputRef}
                      style={{ display: 'none' }}
                      accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
                      onChange={async (e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          setResumeFileName(file.name);
                          const formData = new FormData();
                          formData.append('file', file);
                          setIsUploadingResume(true);
                          try {
                            const res = await axios.post(
                              (app_hostname || '') + '/api/market/jobs/upload_resume',
                              formData,
                              {
                                headers: { 'Content-Type': 'multipart/form-data' },
                              },
                            );
                            if (
                              res.data &&
                              res.data.status === 'Success' &&
                              res.data.data?.file_url
                            ) {
                              setResumeUrl(res.data.data.file_url);
                            }
                          } catch (err) {
                            console.error('Resume upload error:', err);
                          } finally {
                            setIsUploadingResume(false);
                          }
                        }
                      }}
                    />
                    <button
                      type='button'
                      className='btn-choose-file'
                      disabled={isUploadingResume}
                      onClick={() => resumeInputRef.current?.click()}
                    >
                      <i className='mdi mdi-cloud-upload-outline'></i>{' '}
                      {isUploadingResume
                        ? 'กำลังอัปโหลด...'
                        : resumeFileName
                          ? 'เปลี่ยนไฟล์'
                          : 'เลือกไฟล์ Resume'}
                    </button>
                    <span className='selected-file-name'>
                      {resumeFileName || 'ยังไม่ได้เลือกไฟล์ (สมัครได้โดยไม่ต้องแนบไฟล์)'}
                    </span>
                  </div>
                </div>

                <div className='job-apply-policy-check'>
                  <label className='job-apply-policy-label'>
                    <input
                      type='checkbox'
                      id='job-apply-agreeterm'
                      checked={isApplyPolicyAccepted}
                      onChange={(e) => {
                        setIsApplyPolicyAccepted(e.target.checked);
                        if (e.target.checked) setApplyError('');
                      }}
                    />
                    <span>
                      ข้าพเจ้ายอมรับ{' '}
                      <button
                        type='button'
                        className='job-policy-link-inline'
                        onClick={(e) => {
                          e.preventDefault();
                          setIsPolicyModalOpen(true);
                        }}
                      >
                        เงื่อนไขการใช้บริการ
                      </button>{' '}
                      และยินยอมให้ส่งต่อข้อมูลส่วนบุคคลเพื่อการพิจารณาจ้างงาน
                    </span>
                  </label>
                </div>

                <div className='job-apply-modal-actions'>
                  <button
                    type='button'
                    className='btn-apply-cancel'
                    disabled={isSubmittingApply}
                    onClick={() => setIsApplyModalOpen(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type='submit' className='btn-apply-submit' disabled={isSubmittingApply}>
                    {isSubmittingApply ? (
                      <>
                        <i className='mdi mdi-loading mdi-spin'></i> กำลังส่งใบสมัคร...
                      </>
                    ) : (
                      <>
                        <i className='mdi mdi-send'></i> ยืนยันส่งใบสมัคร
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {isPolicyModalOpen && (
        <div className='job-policy-modal-overlay' onClick={() => setIsPolicyModalOpen(false)}>
          <div className='job-policy-modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='job-policy-modal-header'>
              <h6>เงื่อนไขการใช้บริการ (Terms of Use)</h6>
              <button
                type='button'
                className='job-policy-modal-close'
                onClick={() => setIsPolicyModalOpen(false)}
              >
                <i className='mdi mdi-close'></i>
              </button>
            </div>
            <div className='job-policy-modal-body'>
              <Policy />
            </div>
            <div className='job-policy-modal-footer'>
              <button
                type='button'
                className='btn-policy-accept'
                onClick={() => {
                  setIsApplyPolicyAccepted(true);
                  setIsPolicyModalOpen(false);
                  setApplyError('');
                }}
              >
                ยอมรับเงื่อนไข
              </button>
              <button
                type='button'
                className='btn-policy-decline'
                onClick={() => {
                  setIsPolicyModalOpen(false);
                }}
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobDetail;
